/**
 * input/locomotion.ts — In-cabin player locomotion
 *
 * Left thumbstick: smooth head-relative locomotion (planar XZ).
 * Right thumbstick X: snap turn 30° with 300 ms debounce.
 * AABB clamping: 25 cm from cabin walls/consoles.
 *
 * Player rig (THREE.Group) parents camera + controllers.
 * Rig moves within the cabin; cabin never moves.
 */

import * as THREE from 'three';
import { isHandOccupied } from './grab.js';

// ── Rig ────────────────────────────────────────────────────────
let _rig: THREE.Group;
let _camera: THREE.Camera;

// ── Locomotion params ──────────────────────────────────────────
const WALK_SPEED = 1.4;     // m/s
const RUN_SPEED = 2.2;      // m/s at full stick deflection
const DEADZONE = 0.15;
const SNAP_ANGLE = THREE.MathUtils.degToRad(30);
const SNAP_DEBOUNCE_MS = 300;

// ── Cabin AABB (inset from walkable area) ──────────────────────
const CABIN_W = 4.0;
const CABIN_L = 5.0;
const INSET = 0.25; // 25 cm from walls

// Walkable bounds in cabin-local XZ
const BOUNDS = {
  minX: -(CABIN_W / 2) + INSET,
  maxX:  (CABIN_W / 2) - INSET,
  minZ: -(CABIN_L / 2) + INSET,
  maxZ:  (CABIN_L / 2) - INSET,
};

// ── State ──────────────────────────────────────────────────────
let _snapLatched = false;
let _lastSnapTime = 0;
let _currentSpeed = 0; // for HUD

// ── Vignette ───────────────────────────────────────────────────
let _vignetteEl: HTMLElement | null = null;
let _vignetteTarget = 0;

// Temp vectors (module scope — no GC)
const _forward = new THREE.Vector3();
const _right = new THREE.Vector3();
const _move = new THREE.Vector3();
const _up = new THREE.Vector3(0, 1, 0);
const _headPos = new THREE.Vector3();
const _q = new THREE.Quaternion();

export function initLocomotion(rig: THREE.Group, camera: THREE.Camera): void {
  _rig = rig;
  _camera = camera;
  _vignetteEl = document.getElementById('comfort-vignette');
}

/** Update vignette fade toward target opacity */
function _updateVignette(): void {
  if (!_vignetteEl) return;
  const current = parseFloat(_vignetteEl.style.opacity || '0');
  const next = current + (_vignetteTarget - current) * 0.1;
  _vignetteEl.style.opacity = String(next);
}

/** Clamp rig position to cabin walkable bounds */
function _clampRig(): void {
  const pos = _rig.position;
  pos.x = Math.max(BOUNDS.minX, Math.min(BOUNDS.maxX, pos.x));
  pos.z = Math.max(BOUNDS.minZ, Math.min(BOUNDS.maxZ, pos.z));
  // Y stays at 0 — real standing height from local-floor
}

/** Snap turn around head position */
function _snapTurn(angleRad: number): void {
  _camera.getWorldPosition(_headPos);
  _q.setFromAxisAngle(_up, angleRad);

  _rig.position.sub(_headPos).applyQuaternion(_q).add(_headPos);
  _rig.quaternion.premultiply(_q);
}

/**
 * Update locomotion from InputFrame.
 * Called every frame when in XR.
 */
export function updateLocomotion(
  dtSec: number,
  input: { leftStickX: number; leftStickY: number; rightStickX: number; rightStickY: number },
  nowMs: number,
): { speed: number; moving: boolean } {
  const leftOccupied = isHandOccupied('left');
  const rightOccupied = isHandOccupied('right');

  let moving = false;
  let speed = 0;

  // ── Smooth locomotion (left thumbstick, hands-empty only) ────
  if (!leftOccupied) {
    let lx = input.leftStickX;
    let ly = input.leftStickY;

    // Deadzone
    if (Math.abs(lx) < DEADZONE) lx = 0;
    if (Math.abs(ly) < DEADZONE) ly = 0;

    if (lx !== 0 || ly !== 0) {
      // Head-relative basis
      _camera.getWorldDirection(_forward);
      _forward.y = 0;
      _forward.normalize();
      _right.crossVectors(_forward, _up).normalize();

      _move.set(0, 0, 0);
      _move.addScaledVector(_forward, -ly); // forward = -Y
      _move.addScaledVector(_right, lx);

      if (_move.lengthSq() > 1) _move.normalize();

      // Speed: walk at low deflection, run at full
      const deflection = Math.min(1, Math.sqrt(lx * lx + ly * ly));
      speed = WALK_SPEED + (RUN_SPEED - WALK_SPEED) * Math.max(0, (deflection - DEADZONE) / (1 - DEADZONE));

      _rig.position.addScaledVector(_move, speed * dtSec);
      _clampRig();
      moving = true;
    }
  }

  _currentSpeed = moving ? speed : 0;
  _vignetteTarget = moving ? 0.20 : 0;
  _updateVignette();

  // ── Snap turn (right thumbstick X, hands-empty only) ─────────
  if (!rightOccupied) {
    const rx = input.rightStickX;
    const canSnap = (nowMs - _lastSnapTime) > SNAP_DEBOUNCE_MS;

    if (Math.abs(rx) > 0.7 && !_snapLatched && canSnap) {
      _snapTurn(rx > 0 ? -SNAP_ANGLE : SNAP_ANGLE);
      _snapLatched = true;
      _lastSnapTime = nowMs;
    } else if (Math.abs(rx) < 0.3) {
      _snapLatched = false;
    }
  }

  return { speed, moving };
}

/** Get rig local position for HUD */
export function getRigPosition(): THREE.Vector3 {
  return _rig.position.clone();
}

/** Get current locomotion speed for HUD */
export function getCurrentSpeed(): number {
  return _currentSpeed;
}

/** Debug: teleport rig to cabin center */
export function resetRigPosition(): void {
  _rig.position.set(0, 0, 1.5);
  _rig.quaternion.identity();
}
